import { useCallback, useEffect, useId, useRef, useState, type ComponentPropsWithRef, type ReactNode } from "react";
import { AnimatedCopyIcon, AnimatedEyeIcon } from "@one-nexty/icons";

import { cn } from "../../lib/cn";
import { Badge, type BadgeProps } from "../badge";
import { Button } from "../button";
import { CodeBlock } from "../code";
import { SearchInput } from "../search-input";

export type LogLevel = "info" | "warn" | "error" | "debug" | "trace";

export interface LogEntry {
  id: string;
  timestamp: string;
  level: LogLevel;
  message: string;
  /** Métadonnées JSON structurées associées au log (context, payload, tracing ID). */
  meta?: Record<string, unknown>;
}

export interface LogViewerProps extends Omit<ComponentPropsWithRef<"div">, "title"> {
  logs: LogEntry[];
  /** Titre ou nom du service (ex: "api-server / production"). */
  title?: ReactNode;
  /** Active/désactive le défilement automatique vers le dernier log reçu. */
  autoScroll?: boolean;
  onAutoScrollChange?: (enabled: boolean) => void;
  /** Action personnalisée dans l'en-tête (ex: bouton "Vider", "Télécharger .log"). */
  actions?: ReactNode;
  /** Hauteur maximale du composant (défaut: 480px). */
  maxHeight?: string | number;
}

const LEVEL_STYLING: Record<LogLevel, { tone: BadgeProps["tone"]; label: string; text: string }> = {
  info: { tone: "info", label: "INFO", text: "text-fg" },
  warn: { tone: "warning", label: "WARN", text: "text-warning-fg" },
  error: { tone: "danger", label: "ERR", text: "text-danger-fg font-semibold" },
  debug: { tone: "neutral", label: "DBG", text: "text-fg-muted" },
  trace: { tone: "neutral", label: "TRC", text: "text-fg-subtle" },
};

/**
 * Console de logs en temps réel pour infrastructure cloud.
 *
 * ── Garanties de performance & Ergonomie ──────────────────────────────────
 * 1. Recherche instantanée et filtrage par gravité (ERR/WARN/INFO).
 * 2. Live tailing auto-désactivé au défilement manuel vers le haut.
 * 3. Repose à 100% sur les composants propriétaires (Button, Badge, CodeBlock).
 */
export function LogViewer({
  logs,
  title = "Flux de logs",
  autoScroll: externalAutoScroll,
  onAutoScrollChange,
  actions,
  maxHeight = 480,
  className,
  ...props
}: LogViewerProps) {
  const [filterLevel, setFilterLevel] = useState<LogLevel | "all">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [wordWrap, setWordWrap] = useState(false);
  const [internalAutoScroll, setInternalAutoScroll] = useState(true);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputId = useId();

  const autoScroll = externalAutoScroll ?? internalAutoScroll;

  const setAutoScroll = useCallback(
    (enabled: boolean) => {
      if (externalAutoScroll === undefined) setInternalAutoScroll(enabled);
      onAutoScrollChange?.(enabled);
    },
    [externalAutoScroll, onAutoScrollChange],
  );

  const filteredLogs = logs.filter((log) => {
    if (filterLevel !== "all" && log.level !== filterLevel) return false;
    if (searchQuery.trim() !== "") {
      const q = searchQuery.toLowerCase();
      const matchesMsg = log.message.toLowerCase().includes(q);
      const matchesMeta = log.meta ? JSON.stringify(log.meta).toLowerCase().includes(q) : false;
      return matchesMsg || matchesMeta;
    }
    return true;
  });

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [logs, autoScroll, filterLevel, searchQuery]);

  const handleScroll = () => {
    if (!containerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 20;

    if (!isAtBottom && autoScroll) {
      setAutoScroll(false);
    } else if (isAtBottom && !autoScroll) {
      setAutoScroll(true);
    }
  };

  const copyLogLine = (log: LogEntry) => {
    const formatted = `[${log.timestamp}] [${log.level.toUpperCase()}] ${log.message}${
      log.meta ? `\n${JSON.stringify(log.meta, null, 2)}` : ""
    }`;
    navigator.clipboard.writeText(formatted);
    setCopiedId(log.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <div
      className={cn(
        "flex flex-col rounded-xl border border-border bg-surface-sunken overflow-hidden font-mono text-xs shadow-sm",
        className,
      )}
      {...props}
    >
      {/* En-tête du viewer */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-surface-raised px-4 py-3">
        <div className="flex items-center gap-2.5">
          <span className="relative flex size-2 shrink-0">
            <span
              className={cn(
                "absolute inline-flex size-full rounded-full opacity-75",
                autoScroll ? "animate-ping bg-success-fg" : "bg-warning-fg",
              )}
            />
            <span
              className={cn(
                "relative inline-flex size-full rounded-full",
                autoScroll ? "bg-success-fg" : "bg-warning-fg",
              )}
            />
          </span>
          <span className="font-sans text-sm font-semibold text-fg">{title}</span>
          <span className="font-sans text-xs text-fg-muted tabular-nums">
            ({filteredLogs.length} / {logs.length} événements)
          </span>
        </div>

        {/* Filtres & Contrôles via composants propriétaires */}
        <div className="flex flex-wrap items-center gap-2">
          <SearchInput
            id={searchInputId}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filtrer les logs..."
            className="w-48 font-sans text-xs"
          />

          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value as LogLevel | "all")}
            className="h-8 rounded-lg border border-border bg-surface-raised px-2.5 font-sans text-xs font-medium text-fg shadow-xs transition-colors hover:border-border-strong focus-visible:outline-2 focus-visible:outline-ring cursor-pointer"
            aria-label="Filtrer par niveau de gravité"
          >
            <option value="all">Tous niveaux</option>
            <option value="error">Erreurs (ERR)</option>
            <option value="warn">Avertissements (WARN)</option>
            <option value="info">Information (INFO)</option>
            <option value="debug">Débogage (DBG)</option>
            <option value="trace">Trace (TRC)</option>
          </select>

          <Button
            size="sm"
            variant={wordWrap ? "secondary" : "ghost"}
            onClick={() => setWordWrap(!wordWrap)}
            title="Retour à la ligne automatique"
            className="font-sans text-xs"
          >
            Wrap
          </Button>

          <Button
            size="sm"
            variant={autoScroll ? "primary" : "outline"}
            onClick={() => setAutoScroll(!autoScroll)}
            className="font-sans text-xs"
          >
            {autoScroll ? "Tail Live" : "Pause Tail"}
          </Button>

          {actions}
        </div>
      </div>

      {/* Zone de streaming des logs */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        style={{ maxHeight }}
        tabIndex={0}
        role="region"
        aria-label="Flux de journaux d'événements"
        className="overflow-x-auto overflow-y-auto p-3 scrollbar-thin focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring"
      >
        {filteredLogs.length === 0 ? (
          <div className="flex h-32 flex-col items-center justify-center gap-2 font-sans text-fg-subtle">
            <span>Aucun log trouvé avec les filtres actuels.</span>
          </div>
        ) : (
          <div className="divide-y divide-border/20">
            {filteredLogs.map((log, index) => {
              const cfg = LEVEL_STYLING[log.level];
              const isExpanded = expandedLogId === log.id;
              const isCopied = copiedId === log.id;

              return (
                <div
                  key={log.id}
                  className={cn(
                    "group relative flex flex-col py-1 transition-colors hover:bg-surface-raised/60",
                    isExpanded && "bg-surface-raised/40",
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span className="w-8 shrink-0 text-right text-fg-subtle/50 select-none">
                      {index + 1}
                    </span>

                    <span className="shrink-0 text-fg-subtle select-none tabular-nums">
                      {log.timestamp}
                    </span>

                    {/* Badge propriétaire */}
                    <Badge tone={cfg.tone} size="sm" className="shrink-0 font-mono text-[10px]">
                      {cfg.label}
                    </Badge>

                    <span
                      className={cn(
                        "flex-1 leading-relaxed text-fg select-text",
                        cfg.text,
                        wordWrap ? "break-all whitespace-pre-wrap" : "whitespace-nowrap",
                      )}
                    >
                      {log.message}
                    </span>

                    {/* Actions de ligne avec icônes animées */}
                    <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center gap-1 shrink-0 bg-surface-raised px-1 rounded border border-border">
                      {log.meta && (
                        <Button
                          type="button"
                          size="xs"
                          variant="ghost"
                          onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                          title={isExpanded ? "Masquer les métadonnées JSON" : "Voir les métadonnées JSON"}
                        >
                          <AnimatedEyeIcon visible={isExpanded} size={14} />
                          JSON
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="icon-xs"
                        variant="ghost"
                        onClick={() => copyLogLine(log)}
                        title={isCopied ? "Ligne copiée !" : "Copier la ligne de log"}
                        aria-label="Copier la ligne de log"
                      >
                        <AnimatedCopyIcon copied={isCopied} size={14} />
                      </Button>
                    </div>
                  </div>

                  {log.meta && isExpanded && (
                    <div className="mt-2 ml-14">
                      <CodeBlock>{JSON.stringify(log.meta, null, 2)}</CodeBlock>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
